/**
 * Supabase client configuration
 *
 * This file sets up the Supabase client with TypeScript types.
 * The client runs in the browser as part of the static SPA.
 *
 * Environment variables (BUN_PUBLIC_*) are injected at build time by Bun's bundler.
 *
 * For local development:
 *   - Set values in .env.local (Bun loads this automatically)
 */

import type { Database, Json } from '@/types/database.types';
import type { Clerk } from '@clerk/clerk-js';
import type { SupabaseClient } from '@supabase/supabase-js';
import { createClient } from '@supabase/supabase-js';
import { isE2EMode } from '@/lib/e2e';
import { startRealtimeTokenRefresh } from '@/lib/realtimeAuth';

declare global {
  interface Window {
    Clerk?: Clerk;
    // Set by Cypress (window:before:load) to a locally-signed Supabase JWT so E2E
    // requests satisfy RLS without a Clerk session. See cypress/support/e2e.ts.
    __E2E_SUPABASE_TOKEN?: string;
  }
}

// Environment variables injected at build time by Bun's bundler (--env='BUN_PUBLIC_*')
// These get replaced with literal strings at build time, not evaluated at runtime
const rawSupabaseUrl = process.env.BUN_PUBLIC_SUPABASE_URL;
const rawSupabaseKey = process.env.BUN_PUBLIC_SUPABASE_PUBLISHABLE_KEY;

// Debug: log what was actually injected at build time
if (typeof window !== 'undefined') {
  console.log('[Supabase] Raw URL from build:', JSON.stringify(rawSupabaseUrl));
  console.log('[Supabase] Raw Key from build:', JSON.stringify(rawSupabaseKey));
}

// Apply fallbacks
let supabaseApiUrl = rawSupabaseUrl || 'http://localhost:54321';
let supabaseAnonKey = rawSupabaseKey || '';


if (typeof window !== 'undefined') {
  console.log('[Supabase] URL:', supabaseApiUrl);
  if (!rawSupabaseUrl) {
    console.warn('[Supabase] WARNING: BUN_PUBLIC_SUPABASE_URL was not set at build time, using localhost');
  }
  if (!rawSupabaseKey) {
    console.warn('[Supabase] WARNING: BUN_PUBLIC_SUPABASE_PUBLISHABLE_KEY was not set at build time');
  }
}

export async function getAccessToken(): Promise<string | null> {
  if (typeof window === 'undefined') return null;
  // In E2E there is no Clerk session; Cypress injects a locally-signed Supabase JWT
  // (role=authenticated + an app-user email) so requests still pass RLS.
  if (isE2EMode()) {
    return window.__E2E_SUPABASE_TOKEN ?? null;
  }
  return (await window.Clerk?.session?.getToken()) ?? null;
}

export function getSupabaseFunctionUrl(functionPath: string): string {
  return new URL(`/functions/v1/${functionPath.replace(/^\/+/, '')}`, supabaseApiUrl).toString();
}

/**
 * Supabase client instance with TypeScript types
 *
 * Usage:
 * ```typescript
 * import { supabase } from '@/lib/supabase';
 *
 * // Query data
 * const { data, error } = await supabase
 *   .from('projects')
 *   .select('*')
 *   .eq('deal_stage', 'Won');
 *
 * // Insert data
 * const { data, error } = await supabase
 *   .from('projects')
 *   .insert({ name: 'New Project', ... });
 * ```
 */
export let supabase: SupabaseClient<Database> = createClient<Database>(supabaseApiUrl, supabaseAnonKey, {
  accessToken: getAccessToken,
});

// Keeps the realtime socket's token ahead of its expiry; see realtimeAuth.
let stopRealtimeTokenRefresh = startRealtimeTokenRefresh(supabase);

// Event name for React to listen to when supabase is reinitialized
export const SUPABASE_REINIT_EVENT = 'supabase:reinit';

/**
 * Reinitialize the Supabase client with new credentials
 * Call from browser console: window.reinitializeSupabase('https://...', 'key...')
 * React components can listen to the 'supabase:reinit' event to re-render
 */
export function reinitializeSupabase(url: string, anonKey: string): void {
  console.log('[Supabase] Reinitializing with:', { url, anonKey: anonKey.substring(0, 10) + '...' });

  supabaseApiUrl = url;
  supabaseAnonKey = anonKey;

  stopRealtimeTokenRefresh();
  supabase = createClient<Database>(url, anonKey, {
    accessToken: getAccessToken,
  });
  stopRealtimeTokenRefresh = startRealtimeTokenRefresh(supabase);

  // Dispatch event so React components can react
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SUPABASE_REINIT_EVENT, { detail: { url } }));
    console.log('[Supabase] Reinitialized successfully. Dispatched event:', SUPABASE_REINIT_EVENT);
  }
}

// Expose on window for debugging from browser console
if (typeof window !== 'undefined') {
  (window as unknown as { reinitializeSupabase: typeof reinitializeSupabase }).reinitializeSupabase = reinitializeSupabase;
}

/**
 * Helper function to create a server-side Supabase client
 * Use this for server-side operations that need different auth context
 */
export function createServerClient(): SupabaseClient<Database> {
  return createClient<Database>(supabaseApiUrl, supabaseAnonKey, {
    accessToken: getAccessToken,
  });
}

/**
 * Type-safe query builders for common operations
 */
export const db = {
  // Per-user app preferences (Settings > User Settings). One row per employee.
  userSettings: {
    // maybeSingle so a user with no saved settings yet resolves to null (→ defaults)
    // rather than a PGRST116 error.
    getByEmployee: (employeeId: string) =>
      supabase.from('user_settings').select('*').eq('employee_id', employeeId).maybeSingle(),
    // Upsert on the employee_id PK so the first save creates the row and later
    // saves update it. Bump updated_at explicitly since there's no DB trigger.
    upsert: (
      employeeId: string,
      updates: Omit<Database['public']['Tables']['user_settings']['Update'], 'employee_id'>,
    ) =>
      supabase
        .from('user_settings')
        .upsert(
          { employee_id: employeeId, ...updates, updated_at: new Date().toISOString() },
          { onConflict: 'employee_id' },
        )
        .select()
        .single(),
  },

  // Out-of-office (OOO) hours. Visual-only per-employee, per-week.
  employeeOoo: {
    getAll: () => supabase.from('employee_ooo').select('*'),
    // Upsert on (employee_id, week_start) so the first save creates the row and
    // later edits update it. No DB trigger sets updated_at on conflict-update via
    // upsert, so bump it explicitly (matches userSettings.upsert).
    upsert: (employeeId: string, weekStart: string, hours: number) =>
      supabase
        .from('employee_ooo')
        // source: 'manual' stamps this week as hand-entered so the Google Calendar
        // sync (sync-ooo-from-gcal) leaves it alone thereafter ("manual wins"). A
        // human edit to a previously-synced week flips it from 'google_calendar'.
        .upsert(
          { employee_id: employeeId, week_start: weekStart, hours, source: 'manual', updated_at: new Date().toISOString() },
          { onConflict: 'employee_id,week_start' },
        )
        .select()
        .single(),
    delete: (employeeId: string, weekStart: string) =>
      supabase
        .from('employee_ooo')
        .delete()
        .eq('employee_id', employeeId)
        .eq('week_start', weekStart),
  },

  // Offices
  offices: {
    getAll: () => supabase.from('offices').select('*'),
    getActive: () => supabase.from('offices').select('*').eq('is_active', true),
    getById: (id: string) => supabase.from('offices').select('*').eq('id', id).single(),
    create: (office: Database['public']['Tables']['offices']['Insert']) =>
      supabase.from('offices').insert(office).select().single(),
    update: (id: string, updates: Database['public']['Tables']['offices']['Update']) =>
      supabase.from('offices').update(updates).eq('id', id).select().single(),
    delete: (id: string) => supabase.from('offices').delete().eq('id', id),
  },

  // Employees
  employees: {
    getAll: () => supabase.from('employees').select('*'),
    getActive: () => supabase.from('employees').select('*').eq('is_active', true),
    getActiveOrWithAssignments: async () => {
      // Get all active employees
      const { data: activeEmployees, error: activeError } = await supabase
        .from('employees')
        .select('*')
        .eq('is_active', true);

      if (activeError) {
        return { data: null, error: activeError };
      }

      // Get all employee IDs that have assignments
      const { data: assignmentEmployees, error: assignmentError } = await supabase
        .from('assignment_employees')
        .select('employee_id');

      if (assignmentError) {
        return { data: null, error: assignmentError };
      }

      // Collect all employee IDs with assignments
      const employeeIdsWithAssignments = new Set<string>();
      (assignmentEmployees || []).forEach((ae) => {
        if (ae.employee_id) employeeIdsWithAssignments.add(ae.employee_id);
      });

      // Get employees with assignments who aren't already in activeEmployees
      const inactiveEmployeeIds = Array.from(employeeIdsWithAssignments).filter(
        (id) => !activeEmployees?.some((emp) => emp.id === id)
      );

      if (inactiveEmployeeIds.length === 0) {
        return { data: activeEmployees || [], error: null };
      }

      // Fetch inactive employees who have assignments
      const { data: inactiveEmployees, error: inactiveError } = await supabase
        .from('employees')
        .select('*')
        .in('id', inactiveEmployeeIds);

      if (inactiveError) {
        return { data: null, error: inactiveError };
      }

      // Combine and return
      const allEmployees = [
        ...(activeEmployees || []),
        ...(inactiveEmployees || []),
      ];
      return { data: allEmployees, error: null };
    },
    getById: (id: string) => supabase.from('employees').select('*').eq('id', id).single(),
    // maybeSingle so a missing employee row resolves to null (→ NoAccess "not invited")
    // instead of a PGRST116 error (→ AccessError "something went wrong").
    getByEmail: (email: string) => supabase.from('employees').select('*').eq('email', email).maybeSingle(),
    getByOffice: (officeId: string) => supabase.from('employees').select('*').eq('office_id', officeId),
    create: (employee: Database['public']['Tables']['employees']['Insert']) =>
      supabase.from('employees').insert(employee).select().single(),
    update: (id: string, updates: Database['public']['Tables']['employees']['Update']) =>
      supabase.from('employees').update(updates).eq('id', id).select().single(),
    delete: (id: string) => supabase.from('employees').delete().eq('id', id),
  },

  // Deal stages
  dealStages: {
    getAll: () =>
      supabase.from('deal_stages').select('*').order('likelihood_to_close', { ascending: true }),
    create: (dealStage: Database['public']['Tables']['deal_stages']['Insert']) =>
      supabase.from('deal_stages').insert(dealStage).select().single(),
    update: (id: string, updates: Database['public']['Tables']['deal_stages']['Update']) =>
      supabase.from('deal_stages').update(updates).eq('id', id).select().single(),
    delete: (id: string) => supabase.from('deal_stages').delete().eq('id', id),
  },

  categories: {
    getAll: () =>
      supabase.from('categories').select('*').order('sort_order', { ascending: true }),
    create: (category: Database['public']['Tables']['categories']['Insert']) =>
      supabase.from('categories').insert(category).select().single(),
    update: (id: string, updates: Database['public']['Tables']['categories']['Update']) =>
      supabase.from('categories').update(updates).eq('id', id).select().single(),
    delete: (id: string) => supabase.from('categories').delete().eq('id', id),
  },

  // Projects
  projects: {
    getAll: () =>
      supabase.from('projects').select('*, deal_stage:deal_stages ( id, name, likelihood_to_close, kind )'),
    getById: (id: string) =>
      supabase
        .from('projects')
        .select('*, deal_stage:deal_stages ( id, name, likelihood_to_close, kind )')
        .eq('id', id)
        .single(),
    getSummaries: () => supabase.from('project_summaries').select('*'),
    getByClient: (clientId: string) =>
      supabase.from('projects').select('*').eq('client_id', clientId),
    create: (project: Database['public']['Tables']['projects']['Insert']) =>
      supabase.from('projects').insert(project).select().single(),
    update: (id: string, updates: Database['public']['Tables']['projects']['Update']) =>
      supabase.from('projects').update(updates).eq('id', id).select().single(),
    // Atomic: updates the project AND reconciles every child assignment's
    // weekly_hours_allocated array to num_weeks in one transaction. Returns
    // { old, new, resized: [{ id, old, new }] } so callers can audit accurately.
    updateWithReindex: (id: string, updates: Database['public']['Tables']['projects']['Update']) =>
      supabase.rpc('update_project_with_reindex', { p_id: id, p_updates: updates as unknown as Json }),
    delete: (id: string) => supabase.from('projects').delete().eq('id', id),
  },

  // Clients
  clients: {
    getAll: () => supabase.from('clients').select('*').order('name', { ascending: true }),
    getById: (id: string) => supabase.from('clients').select('*').eq('id', id).single(),
    create: (client: Database['public']['Tables']['clients']['Insert']) =>
      supabase.from('clients').insert(client).select().single(),
    update: (id: string, updates: Database['public']['Tables']['clients']['Update']) =>
      supabase.from('clients').update(updates).eq('id', id).select().single(),
    delete: (id: string) => supabase.from('clients').delete().eq('id', id),
  },

  // Project Assignments
  projectAssignments: {
    getAll: () => supabase.from('project_assignments').select('*'),
    getActive: () => supabase.from('active_project_assignments').select('*'),
    getById: (id: string) => supabase.from('project_assignments').select('*').eq('id', id).single(),
    getByEmployee: (employeeId: string) =>
      supabase
        .from('project_assignments')
        .select('*, assignment_employees!inner(employee_id)')
        .eq('assignment_employees.employee_id', employeeId),
    getByProject: (projectId: string) =>
      supabase.from('project_assignments').select('*').eq('project_id', projectId),
    create: (assignment: Database['public']['Tables']['project_assignments']['Insert']) =>
      supabase.from('project_assignments').insert(assignment).select().single(),
    update: (id: string, updates: Database['public']['Tables']['project_assignments']['Update']) =>
      supabase.from('project_assignments').update(updates).eq('id', id).select().single(),
    // Atomic: applies the column patch AND (when replaceEmployees) replaces the
    // assignment's employee links in one transaction, so a failed relink can't
    // leave the slot empty. Returns { old, new, old_employee_ids, new_employee_ids }.
    updateWithEmployees: (
      id: string,
      cols: Database['public']['Tables']['project_assignments']['Update'],
      employeeIds: string[],
      replaceEmployees: boolean,
    ) =>
      supabase.rpc('update_assignment_with_employees', {
        p_id: id,
        p_cols: cols as unknown as Json,
        p_employee_ids: employeeIds,
        p_replace_employees: replaceEmployees,
      }),
    delete: (id: string) => supabase.from('project_assignments').delete().eq('id', id),
  },

  // Roles
  roles: {
    getAll: () => supabase.from('roles').select('*').order('sort_order'),
    getById: (id: string) => supabase.from('roles').select('*').eq('id', id).single(),
    create: (role: Database['public']['Tables']['roles']['Insert']) =>
      supabase.from('roles').insert(role).select().single(),
    update: (id: string, updates: Database['public']['Tables']['roles']['Update']) =>
      supabase.from('roles').update(updates).eq('id', id).select().single(),
    delete: (id: string) => supabase.from('roles').delete().eq('id', id),
  },
};
